/**
 * Labels + candidate API keys for each row on the Unpaid Payments V2 partner tiles.
 *
 * - **keys**: tried in order; the first present, non-null/non-empty value wins.
 * - **Dots** in a key walk nested objects (e.g. `summary.total_amount`).
 * - Add or reorder keys per partner when backend fields are confirmed.
 * - Set a partner to **`[]`** to hide tile metrics for that partner entirely.
 */
export type PartnerTileValueFormat = 'currency' | 'number' | 'text';

export interface PartnerTileMetricDefinition {
  label: string;
  keys: string[];
  format?: PartnerTileValueFormat;
}

export interface PartnerOverviewMetricKeys {
  availableBalance: string[];
  upcomingAmount: string[];
  totalPaymentToPay: string[];
  totalPaymentsCount: string[];
  urgentPaymentsAmount: string[];
  urgentPaymentsCount: string[];
}

const FM_LIKE_LIST_METRICS: PartnerTileMetricDefinition[] = [
  { label: 'Total amount', keys: ['total_amount', 'totalAmount'], format: 'currency' },
  { label: 'Available balance', keys: ['available_balance', 'availableBalance'], format: 'currency' },
];

/** Broad guesses for simple `payment/unpaid_payments_list` style responses — tighten keys when APIs are known. */
const SIMPLE_LIST_METRICS_GUESS: PartnerTileMetricDefinition[] = [
  {
    label: 'Total amount',
    keys: [
      'total_amount',
      'totalAmount',
      'total_amount_to_pay',
      'totalAmountToPay',
      'payment_due_total',
    ],
    format: 'currency',
  },
  {
    label: 'Available balance',
    keys: ['available_balance', 'availableBalance', 'balance', 'partner_balance'],
    format: 'currency',
  },
];

const LENDEE_TILE_METRICS: PartnerTileMetricDefinition[] = [
  ...SIMPLE_LIST_METRICS_GUESS,
  { label: 'Platform balance', keys: ['platform_balance', 'platformBalance'], format: 'currency' },
];

export const UNPAID_PARTNER_TILE_METRICS: Record<string, PartnerTileMetricDefinition[]> = {
  lendee: LENDEE_TILE_METRICS,
  credee: FM_LIKE_LIST_METRICS,
  recuvery: FM_LIKE_LIST_METRICS,
  fm: FM_LIKE_LIST_METRICS,
};

const DEFAULT_OVERVIEW_KEYS: PartnerOverviewMetricKeys = {
  availableBalance: ['available_balance', 'availableBalance'],
  upcomingAmount: ['upcoming_balance', 'upcomingBalance', 'pending_balance', 'pendingBalance'],
  totalPaymentToPay: ['total_amount', 'totalAmount', 'total_amount_to_pay', 'payment_due_total'],
  totalPaymentsCount: ['count', 'number_of_records', 'total_records', 'payments.length'],
  urgentPaymentsAmount: ['urgent_balance', 'urgent_amount', 'total_urgent_amount'],
  urgentPaymentsCount: ['total_urgent_payments', 'urgent_count'],
};

export const UNPAID_PARTNER_OVERVIEW_KEYS: Record<string, PartnerOverviewMetricKeys> = {
  lendee: {
    ...DEFAULT_OVERVIEW_KEYS,
    // Total to pay = sum of released row amounts (patched in component); platform_balance is upcoming only.
    totalPaymentToPay: [
      'released_payment_amount',
      'released_payments_amount',
      'payable_amount',
      'total_payable_amount',
      'total_pay_to_users',
      'total_pay_to_user',
      'released_pay_to_user',
      'released_pay_to_users',
      'uspa_ids.released_payment_amount',
      'uspa_ids.released_amount',
      'uspa_ids.released_pay_to_user',
      'uspaIds.released_payment_amount',
      'uspaIds.released_amount',
      'uspaIds.released_pay_to_user',
    ],
    totalPaymentsCount: [
      'released_payment_count',
      'released_payments_count',
      'released_count',
      'uspa_ids.released_count',
      'uspa_ids.released_payment_count',
      'uspaIds.released_count',
      'uspaIds.released_payment_count',
    ],
    upcomingAmount: [...DEFAULT_OVERVIEW_KEYS.upcomingAmount, 'platform_balance', 'platformBalance'],
  },
  credee: DEFAULT_OVERVIEW_KEYS,
  recuvery: DEFAULT_OVERVIEW_KEYS,
  fm: DEFAULT_OVERVIEW_KEYS,
};

/** `get_lendee_unpaid_list` overview fields (Credee filter 16, FM filter 15). */
export const LENDEE_UNPAID_OVERVIEW_KEYS: PartnerOverviewMetricKeys = {
  availableBalance: ['totalStripeBalance'],
  upcomingAmount: ['credee_wallet_amount'],
  totalPaymentToPay: ['total_payout_amount'],
  totalPaymentsCount: ['pagination.total', 'count', 'total_records', 'number_of_records'],
  urgentPaymentsAmount: ['urgent_amount', 'urgent_balance', 'total_urgent_amount'],
  urgentPaymentsCount: ['urgent_data_count'],
};

/**
 * Count/amount overview tiles reflect the paginated table's current page (`payments()`),
 * not list-response totals or `get_urgent_payment_data`.
 */
export const UNPAID_PARTNER_OVERVIEW_FROM_VISIBLE_TABLE = new Set<string>([]);

/** Overview payment count is patched from payable rows (`isRowPayable`), not list `count`. */
export const UNPAID_PARTNER_OVERVIEW_PAYABLE_COUNT = new Set<string>(['credee', 'fm', 'recuvery']);
